import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';

const API_URL = process.env.REACT_APP_STRAPI_API_URL || 'https://www.eatmeatapp.com';

function resolveImage(img: any): string | null {
  if (!img) return null;
  const url = img.url || img.formats?.small?.url || img.formats?.medium?.url;
  if (!url) return null;
  return url.startsWith('http') ? url : `${API_URL}${url}`;
}

const MEAL_TINTS: Record<string, string> = {
  Breakfast: 'bg-amber-50 text-amber-800',
  Lunch: 'bg-green-50 text-green-800',
  Dinner: 'bg-purple-50 text-purple-800',
  Snack: 'bg-blue-50 text-blue-800',
};

function fmtTime(t?: string | null): string {
  if (!t) return '';
  // Strapi returns "HH:MM:SS.sss"
  const m = /^(\d{2}):(\d{2})/.exec(t);
  if (!m) return t;
  const h = parseInt(m[1], 10);
  const mm = m[2];
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = ((h + 11) % 12) + 1;
  return `${h12}:${mm} ${period}`;
}

function prettyFasting(type?: string): string {
  if (!type) return '';
  return type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function MealPlanPreviewPage() {
  const { documentId } = useParams<{ documentId: string }>();
  const [params] = useSearchParams();
  const secret = params.get('secret') || '';
  const status = (params.get('status') === 'draft' ? 'draft' : 'published') as 'draft' | 'published';

  const [plan, setPlan] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!documentId || !secret) {
      setError('Missing preview parameters.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    axios
      .get(`${API_URL}/api/general-meal-plans/preview/${documentId}`, {
        params: { secret, status },
      })
      .then((res) => setPlan(res.data?.data || null))
      .catch((e) => setError(e?.response?.data?.error || 'Failed to load preview'))
      .finally(() => setLoading(false));
  }, [documentId, secret, status]);

  const days = Array.isArray(plan?.daily_plans) ? plan.daily_plans : [];
  // sort by day_number ascending
  const sortedDays = [...days].sort((a: any, b: any) => (a?.day_number || 0) - (b?.day_number || 0));

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Meal plan preview — {plan?.title || documentId}</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className={`w-full px-4 py-2 text-center text-xs font-semibold ${
        status === 'draft'
          ? 'bg-amber-100 text-amber-800 border-b border-amber-200'
          : 'bg-green-100 text-green-800 border-b border-green-200'
      }`}>
        {status === 'draft' ? '⚠ Preview · DRAFT (unpublished)' : '✓ Preview · Published version'}
      </div>

      {loading ? (
        <div className="max-w-3xl mx-auto px-4 py-10">
          <div className="h-8 bg-gray-200 rounded animate-pulse mb-4 w-2/3" />
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-32 bg-gray-100 rounded-2xl animate-pulse" />
            ))}
          </div>
        </div>
      ) : error ? (
        <div className="max-w-md mx-auto px-4 py-16 text-center">
          <div className="text-4xl mb-3">⚠️</div>
          <h1 className="font-bold text-gray-900 text-lg mb-2">Preview unavailable</h1>
          <p className="text-sm text-gray-600">{error}</p>
        </div>
      ) : plan ? (
        <div className="max-w-3xl mx-auto px-4 py-8 space-y-5">
          {/* Header */}
          <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6 md:p-8">
            <div className="inline-block text-[10px] font-bold uppercase tracking-widest text-primary bg-primary/10 rounded-full px-2.5 py-1 mb-3">
              Meal Plan
            </div>
            <h1 className="text-2xl md:text-3xl font-extrabold text-gray-900 leading-tight mb-2">{plan.title}</h1>
            {plan.description && (
              <p className="text-gray-600 leading-relaxed mt-2 whitespace-pre-wrap">{plan.description}</p>
            )}
            <div className="flex flex-wrap gap-2 mt-4 text-xs">
              <span className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 rounded-full px-3 py-1 font-bold">📅 {plan.total_days || sortedDays.length} day{(plan.total_days || sortedDays.length) === 1 ? '' : 's'}</span>
              <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 font-bold ${plan.is_public ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                {plan.is_public ? '🌐 Public' : '🔒 Private'}
              </span>
            </div>
          </div>

          {/* Daily plans */}
          {sortedDays.length === 0 ? (
            <div className="bg-white rounded-2xl border border-gray-100 p-8 text-center text-sm text-gray-500">
              No daily plans defined.
            </div>
          ) : (
            sortedDays.map((day: any, di: number) => {
              const meals = Array.isArray(day.meals) ? day.meals : [];
              const fasts = Array.isArray(day.fasting_periods) ? day.fasting_periods : [];
              return (
                <div key={di} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                  <div className="flex items-baseline justify-between mb-3">
                    <h2 className="font-bold text-gray-900">
                      Day {day.day_number ?? di + 1}
                      {day.date && (
                        <span className="text-xs text-gray-400 font-normal ml-2">
                          · {new Date(day.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                        </span>
                      )}
                    </h2>
                    <span className="text-[11px] text-gray-400">{meals.length} meal{meals.length === 1 ? '' : 's'}{fasts.length ? ` · ${fasts.length} fast` : ''}</span>
                  </div>

                  {meals.length > 0 && (
                    <ul className="space-y-2">
                      {meals.map((m: any, mi: number) => {
                        const recipe = m?.recipe;
                        const custom = m?.user_custom_recipe;
                        const title = recipe?.title || custom?.title || '(no recipe linked)';
                        const imgUrl = resolveImage(recipe?.image);
                        return (
                          <li key={mi} className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl">
                            {imgUrl ? (
                              <img src={imgUrl} alt={title} className="w-14 h-14 rounded-lg object-cover shrink-0" />
                            ) : (
                              <div className="w-14 h-14 rounded-lg bg-gradient-to-br from-orange-100 to-red-100 flex items-center justify-center text-2xl shrink-0">🍖</div>
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                {m.meal_type && (
                                  <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${MEAL_TINTS[m.meal_type] || 'bg-gray-100 text-gray-700'}`}>
                                    {m.meal_type}
                                  </span>
                                )}
                                {m.time && <span className="text-[10px] text-gray-400">⏱ {fmtTime(m.time)}</span>}
                                {m.completed && <span className="text-[10px] font-semibold text-green-700">✓ Completed</span>}
                              </div>
                              <div className="text-sm font-semibold text-gray-800 mt-0.5 truncate">{title}</div>
                              {recipe?.cooking_time && (
                                <div className="text-[11px] text-gray-500">⏱ {recipe.cooking_time}{recipe.servings ? ` · 🍽 ${recipe.servings} servings` : ''}</div>
                              )}
                              {m.notes && <p className="text-[11px] text-gray-500 italic mt-1">"{m.notes}"</p>}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}

                  {fasts.length > 0 && (
                    <div className="mt-3">
                      <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Fasting</div>
                      <ul className="space-y-1.5">
                        {fasts.map((f: any, fi: number) => (
                          <li key={fi} className="flex items-center gap-2 p-2.5 bg-indigo-50 rounded-lg">
                            <span className="text-base">⏳</span>
                            <div className="flex-1 min-w-0 text-xs">
                              <span className="font-semibold text-indigo-800">{prettyFasting(f.fasting_type)}</span>
                              {(f.start_time || f.end_time) && (
                                <span className="text-indigo-600 ml-2">
                                  {fmtTime(f.start_time)}{f.end_time ? ` → ${fmtTime(f.end_time)}` : ''}
                                </span>
                              )}
                              {f.completed && <span className="ml-2 text-green-700 font-semibold">✓</span>}
                              {f.notes && <span className="block italic text-indigo-500 mt-0.5">"{f.notes}"</span>}
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              );
            })
          )}

          {/* Footer */}
          <div className="text-center text-[11px] text-gray-400 py-4">
            Preview · documentId <code>{documentId}</code> · status <strong className="uppercase">{status}</strong>
          </div>
        </div>
      ) : null}
    </div>
  );
}
